import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional fallback when an error is caught. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches React render errors so the app shows a fallback instead of a blank screen.
 * User can refresh to recover. See CLAUDE.md for dev note on blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-background text-foreground">
          <h1 className="text-xl font-semibold">Algo ha fallado</h1>
          <p className="text-muted-foreground text-center max-w-md">
            Se ha producido un error. Recarga la página para continuar.
          </p>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Recargar página
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
